import { Button } from '@/components/ui/button.js';
import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import type { ParameterDef } from '@quartup/reports-shared';
import { useEffect, useState } from 'react';
import { resolveDefaultDate } from '../lib/date-expressions.js';
import { ParamInput } from './params/param-input.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ParamValues = Record<string, unknown>;

interface ParamError {
  paramName: string;
  message: string;
}

interface ParamsFormProps {
  parameters: ParameterDef[];
  initialValues?: ParamValues;
  disabled?: boolean;
  paramErrors?: ParamError[];
  onSubmit: (values: ParamValues) => void;
  onReset?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildDefaults(parameters: ParameterDef[]): ParamValues {
  const defaults: ParamValues = {};
  for (const param of parameters) {
    if (param.type === 'date') {
      const def = param.default;
      if (def !== undefined) {
        defaults[param.name] = resolveDefaultDate(String(def));
      } else {
        defaults[param.name] = '';
      }
    } else if (param.type === 'number') {
      defaults[param.name] = param.default ?? '';
    } else if (param.type === 'string') {
      defaults[param.name] = typeof param.default === 'string' ? param.default : '';
    } else if (param.type === 'boolean') {
      defaults[param.name] = param.default ?? '';
    } else if (param.type === 'ref') {
      if (param.multiple === true) {
        defaults[param.name] = Array.isArray(param.default) ? param.default : [];
      } else {
        defaults[param.name] = typeof param.default === 'string' ? param.default : '';
      }
    }
  }
  return defaults;
}

function validate(parameters: ParameterDef[], values: ParamValues): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const param of parameters) {
    if (!param.required) continue;
    const val = values[param.name];
    const isEmpty =
      val === undefined || val === '' || (Array.isArray(val) && val.length === 0) || val === null;
    if (isEmpty) {
      errors[param.name] = t('params.required');
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ParamsForm({
  parameters,
  initialValues,
  disabled = false,
  paramErrors,
  onSubmit,
  onReset,
}: ParamsFormProps) {
  const [values, setValues] = useState<ParamValues>(() =>
    initialValues !== undefined ? { ...initialValues } : buildDefaults(parameters),
  );
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);

  const defaultValues = buildDefaults(parameters);

  // Merge external param errors from server into local error map
  const serverErrors = Object.fromEntries((paramErrors ?? []).map((e) => [e.paramName, e.message]));

  const validationErrors = validate(parameters, values);

  const allErrors: Record<string, string> = {};
  for (const [k, v] of Object.entries(validationErrors)) {
    if (submitted || touched[k] === true) {
      allErrors[k] = v;
    }
  }
  for (const [k, v] of Object.entries(serverErrors)) {
    allErrors[k] = v;
  }

  const isValid = Object.keys(validationErrors).length === 0;

  useEffect(() => {
    if (initialValues !== undefined) {
      setValues(initialValues);
    }
  }, [initialValues]);

  function handleChange(name: string, value: unknown) {
    setValues((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!isValid) return;
    onSubmit(values);
  }

  function handleReset() {
    setValues(defaultValues);
    setTouched({});
    setSubmitted(false);
    onReset?.();
  }

  if (parameters.length === 0) return null;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div
        className={cn(
          'rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
          'bg-[var(--color-qup-bg-card-dark)] p-4 shadow-[var(--shadow-card)]',
        )}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {parameters.map((param) => {
            const labelText = param.label?.es ?? param.name;
            const fieldId = `param-${param.name}`;
            const fieldError = allErrors[param.name];

            return (
              <div key={param.name} className="flex flex-col gap-1">
                <label
                  htmlFor={fieldId}
                  className="text-xs font-medium text-[var(--color-qup-text-muted-dark)]"
                >
                  {labelText}
                  {param.required === true && (
                    <span aria-hidden="true" className="ml-0.5 text-[var(--color-qup-primary)]">
                      *
                    </span>
                  )}
                </label>
                <ParamInput
                  param={param}
                  id={fieldId}
                  value={values[param.name]}
                  onChange={(v) => handleChange(param.name, v)}
                  disabled={disabled}
                  error={fieldError}
                />
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={disabled || (submitted && !isValid)}
            aria-busy={disabled ? 'true' : undefined}
          >
            {disabled ? t('report.executing') : t('report.execute')}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={handleReset} disabled={disabled}>
            {t('report.reset_params')}
          </Button>
        </div>
      </div>
    </form>
  );
}
